import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-hot-toast";

import {
  Globe,
  Mail,
  MapPin,
  Info,
  MessageSquare,
  ExternalLink,
  ShieldCheck,
  Hash,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import useClients from "../../Services/ClientServices/ClientConfig/useClientConfig";
import { Pencil, X, Upload, ChevronDown, Check } from "lucide-react";
import { Listbox } from "@headlessui/react";
import { GlobalContext } from "../../../Context/GlobalContext";
import { uploadItemImage } from "../../../Utils/imageUploadUtils";

/* ---------------- CONSTANTS ---------------- */
const THEME_BLUE_BG = "bg-[#f8fbff]";
const BORDER_SMART = "border-gray-300";

export default function ClientWhatsAppTab({ clientId, clientName }) {
  const { anlookApi } = useContext(GlobalContext);
  const {
    fetchWhatsappMetaProfile: fetchProfile,
    updateWhatsappProfile,
    getWhatsappProfileHandle,
    getWhatsappVerticals,
    getWhatsappProfileDisplayName,
    updateWhatsappProfileDisplayName,
    getWhatsappProfileDisplayNameStatus,
  } = useClients();

  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [verticalOptions, setVerticalOptions] = useState([]);

  const [formData, setFormData] = useState({
    messaging_product: "",
    about: "",
    address: "",
    description: "",
    vertical: "",
    email: "",
    websites: ["", ""],
  });

  const [displayName, setDisplayName] = useState("");
  const [displayNameStatus, setDisplayNameStatus] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!clientId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchProfile(clientId);
        if (!alive) return;
        if (data) setProfile(data);
        else setError("No WhatsApp profile found.");
      } catch (e) {
        if (!alive) return;
        setError("Failed to load WhatsApp profile.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [clientId]);

  useEffect(() => {
    if (profile) {
      setFormData({
        messaging_product: profile.messaging_product || "",
        about: profile.about || "",
        address: profile.address || "",
        description: profile.description || "",
        vertical: profile.vertical || "",
        email: profile.email || "",
        websites: profile.websites
          ?.slice(0, 2)
          .concat(["", ""])
          .slice(0, 2) || ["", ""],
      });
    }
  }, [profile]);

  useEffect(() => {
    const loadVerticals = async () => {
      const data = await getWhatsappVerticals();
      setVerticalOptions(data);
    };

    loadVerticals();
  }, []);

  useEffect(() => {
    const loadDisplayName = async () => {
      if (!clientId) return;

      try {
        const res = await getWhatsappProfileDisplayName(clientId);
        // adjust key if API shape differs
        const name = res?.verified_name || "";
        setDisplayName(name);
        setNameInput(name);
      } catch (e) {
        console.error("Failed to fetch display name");
      }
    };
    const loadDisplayNameStatus = async () => {
      if (!clientId) return;

      try {
        const res = await getWhatsappProfileDisplayNameStatus(clientId);
        // adjust key if API shape differs
        const status = res?.new_name_status || "";
        setDisplayNameStatus(status);
      } catch (e) {
        console.error("Failed to fetch display name status");
      }
    };

    loadDisplayName();
    loadDisplayNameStatus();
  }, [clientId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleWebsiteChange = (index, value) => {
    const updated = [...formData.websites];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, websites: updated }));
  };

  if (loading) return <LoaderSkeleton />;

  if (error)
    return (
      <div
        className={`flex flex-col items-center justify-center min-h-[400px] text-gray-400 ${THEME_BLUE_BG}`}
      >
        <AlertCircle size={40} className="mb-3 opacity-20" />
        <p className="text-sm font-medium">{error}</p>
      </div>
    );

    const handleProfilePictureSelect = async (e) => {
      const file = e.target.files?.[0];

      if (!file) return;

      if (!clientId) {
        toast.error("Client id missing");
        e.target.value = "";
        return;
      }

      try {
        setUploading(true);

        // Local preview
        const localPreview = URL.createObjectURL(file);
        setPreview(localPreview);

        // ==========================================
        // COMMON IMAGE UPLOAD
        // ==========================================

        const uploadResult = await uploadItemImage({
          anlookApi,
          userid: clientId,
          itemName: "whatsapp_profile",
          file,
          maxSizeMB: 5,
        });

        const imageUrl = uploadResult?.imageUrl;

        if (!imageUrl) {
          throw new Error("Image URL missing");
        }

        // ==========================================
        // IMPORTANT:
        // backend currently .webp URL return kar raha hai.
        // Returned file ke according filetype bhejo.
        // ==========================================

        const returnedExtension =
          imageUrl.split(".").pop()?.toLowerCase() || "";

        const uploadedFileType =
          returnedExtension === "webp"
            ? "image/webp"
            : returnedExtension === "png"
              ? "image/png"
              : returnedExtension === "jpg" || returnedExtension === "jpeg"
                ? "image/jpeg"
                : file.type;

        // ==========================================
        // GET WHATSAPP PROFILE HANDLE
        // ==========================================

        const profileHandle = await getWhatsappProfileHandle({
          userid: clientId,

          filename: imageUrl
            .split("/")
            .pop()
            .replace(/\.[^/.]+$/, ""),

          filetype: uploadedFileType,

          fileurl: imageUrl,
        });

        if (!profileHandle) {
          throw new Error("Handle generation failed");
        }

        // ==========================================
        // UPDATE WHATSAPP PROFILE
        // ==========================================

        await updateWhatsappProfile({
          userid: clientId,
          messaging_product: profile?.messaging_product,
          about: profile?.about,
          address: profile?.address,
          description: profile?.description,
          vertical: profile?.vertical,
          email: profile?.email,
          websites: profile?.websites || [],
          profile_picture_handle: profileHandle,
        });

        // Refresh profile
        const updatedProfile = await fetchProfile(clientId);

        if (updatedProfile) {
          setProfile(updatedProfile);
        }

        setIsModalOpen(false);

        toast.success("Profile picture updated");
      } catch (error) {
        console.error("WhatsApp profile image upload failed:", error);

        toast.error(error?.message || "Failed to update profile picture");
      } finally {
        setUploading(false);
        e.target.value = "";
      }
    };

  const getVerticalTitle = (value) => {
    return verticalOptions.find((v) => v.value === value)?.title || value;
  };

  return (
    <div className={`w-full ${THEME_BLUE_BG} pb-3`}>
      {/* HEADER SECTION */}
      <div
        className={`border-b bg-white px-3 py-2.5 shadow-sm ${BORDER_SMART}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-[#075E54]/10">
              <MessageSquare className="text-[#075E54]" size={16} />
            </div>
            <div>
              <h1 className="text-sm font-bold text-gray-900">
                WhatsApp Business Profile
              </h1>
              <p className="text-[10px] font-medium text-gray-500">
                Meta Verified Profile •{" "}
                <span className="font-mono text-xs">{clientId}</span>
              </p>
            </div>
          </div>
          {profile?.vertical && (
            <div className="flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-1">
              <ShieldCheck size={14} className="text-emerald-600" />
              <span className="text-xs font-bold text-emerald-700 uppercase tracking-tight">
                {profile.vertical}
              </span>
            </div>
          )}
        </div>
      </div>

      <main className="max-w-[1800px] px-0 py-3">
        {/* BRAND BANNER SECTION */}
        <div className="relative mb-7 h-24 w-full rounded-xl bg-gradient-to-r from-[#00c57c] to-[#03ac6e] shadow-sm">
          <div className="absolute -bottom-4 left-4 flex items-end gap-3">
            <div className="relative h-20 w-20 overflow-hidden rounded-xl border-2 border-white bg-white shadow-md">
              {profile?.profile_picture_url ? (
                <img
                  src={preview || profile.profile_picture_url}
                  alt="profile"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="grid h-full w-full place-items-center bg-gray-100 text-gray-400">
                  <MessageSquare size={40} />
                </div>
              )}

              {/* EDIT BUTTON */}
              <button
                onClick={() => setIsModalOpen(true)}
                className="absolute bottom-1 right-1 rounded-full bg-white p-1 shadow hover:bg-gray-100"
              >
                <Pencil size={14} />
              </button>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-2">
                {!isEditingName ? (
                  <>
                    <h2 className="text-base font-bold tracking-tight text-white drop-shadow-md">
                      {displayName || "—"}
                    </h2>

                    <button
                      onClick={() => setIsEditingName(true)}
                      className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition"
                    >
                      <Pencil size={14} className="text-white" />
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      className="px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-900 focus:outline-none"
                      autoFocus
                    />

                    <button
                      disabled={savingName}
                      onClick={async () => {
                        try {
                          setSavingName(true);
                          const encodedName = nameInput.replace(/ /g, "%20");
                          await updateWhatsappProfileDisplayName(
                            encodedName,
                            clientId,
                          );

                          setDisplayName(nameInput);
                          setIsEditingName(false);
                          toast.success("Display name updated");
                        } catch (e) {
                          toast.error("Failed to update display name");
                        } finally {
                          setSavingName(false);
                        }
                      }}
                      className="px-2 py-1 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 disabled:opacity-60"
                    >
                      Save
                    </button>
                  </>
                )}
              </div>

              <div className="flex items-center gap-1 text-[10px] font-semibold text-emerald-50/90">
                <CheckCircle2 size={14} className="text-emerald-300" />
                <span>{displayNameStatus || "status not set"}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col rounded-xl border border-[#01bf79] p-2">
          <div className="w-100 justify-end flex">
            <button
              onClick={() => setIsEditProfileOpen(true)}
              className="flex h-7 items-center gap-1 rounded-md bg-[#01bf79] px-2.5 text-[10px] font-bold text-white hover:opacity-90"
            >
              <Pencil size={14} />
              Edit Profile
            </button>
          </div>

          <div className="mt-2 grid grid-cols-1 gap-3 lg:grid-cols-12">
            {/* LEFT COLUMN */}
            <div className="lg:col-span-7 space-y-6">
              <Card title="Business Biography" icon={Info}>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-[9px] font-bold uppercase tracking-wide text-gray-500">
                      Current Status
                    </label>
                    <div
                      className={`rounded-lg bg-white border p-2 ${BORDER_SMART} italic text-gray-800 text-[11px] leading-4 shadow-sm`}
                    >
                      "{profile?.about || "No status set for this business"}"
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-[9px] font-bold uppercase tracking-wide text-gray-500">
                      Category
                    </label>
                    <div
                      className={`rounded-lg bg-white border p-2 ${BORDER_SMART} italic text-gray-800 text-[11px] leading-4 shadow-sm`}
                    >
                      "{getVerticalTitle(profile?.vertical) || "Not set"}"
                    </div>
                  </div>
                  {profile?.description && (
                    <div>
                      <label className="mb-1 block text-[9px] font-bold uppercase tracking-wide text-gray-500">
                        Full Description
                      </label>
                      <p className="text-[15px] text-gray-700 leading-relaxed font-medium">
                        {profile.description}
                      </p>
                    </div>
                  )}
                </div>
              </Card>

              <Card title="Official Links" icon={Globe}>
                <div className="flex flex-wrap gap-4">
                  {profile?.websites?.length > 0 ? (
                    profile.websites.map((url) => (
                      <a
                        key={url}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className={`group flex items-center gap-3 bg-white border ${BORDER_SMART} pl-4 pr-3 py-2.5 rounded-xl hover:border-[#075E54] hover:shadow-md transition-all`}
                      >
                        <span className="text-sm font-bold text-gray-700">
                          {url.replace(/^https?:\/\//, "")}
                        </span>
                        <div className="p-1.5 bg-gray-100 rounded-lg group-hover:bg-[#075E54] group-hover:text-white transition-colors border border-gray-200">
                          <ExternalLink size={14} />
                        </div>
                      </a>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 font-medium italic">
                      No external websites linked.
                    </p>
                  )}
                </div>
              </Card>
            </div>

            {/* RIGHT COLUMN */}
            <div className="lg:col-span-5 space-y-6">
              <Card title="Meta Contact Details" icon={Mail}>
                <div className="space-y-1">
                  <InfoItem
                    icon={Mail}
                    label="Verified Support Email"
                    value={profile?.email}
                  />
                  <div className={`h-px bg-gray-200 my-2 mx-3`} />
                  <InfoItem
                    icon={MapPin}
                    label="Registered Address"
                    value={profile?.address}
                  />
                </div>
              </Card>

              <Card title="System Metadata" icon={Hash}>
                <div className="grid grid-cols-2 gap-2">
                  <div
                    className={`rounded-lg border bg-white p-2 ${BORDER_SMART}`}
                  >
                    <p className="text-[10px] font-bold text-blue-500 uppercase tracking-tight">
                      Messaging Product
                    </p>
                    <p className="text-sm font-bold text-gray-800 mt-1 uppercase">
                      {profile?.messaging_product || "WABA"}
                    </p>
                  </div>
                  <div
                    className={`p-4 rounded-xl bg-white border ${BORDER_SMART}`}
                  >
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-tight">
                      API Status
                    </p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <CheckCircle2 size={16} className="text-emerald-600" />
                      <p className="text-sm font-extrabold text-emerald-700">
                        LIVE
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl relative">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Update Profile Picture</h3>
              <button onClick={() => setIsModalOpen(false)}>
                <X size={18} />
              </button>
            </div>

            {/* Preview */}
            <div className="flex justify-center mb-6">
              <div className="h-32 w-32 rounded-full overflow-hidden border">
                {preview ? (
                  <img src={preview} className="h-full w-full object-cover" />
                ) : (
                  <img
                    src={profile?.profile_picture_url}
                    className="h-full w-full object-cover"
                  />
                )}
              </div>
            </div>

            {/* Upload */}
            <label className="flex items-center justify-center gap-2 cursor-pointer border-2 border-dashed rounded-xl p-4 text-sm font-semibold text-gray-600 hover:border-[#075E54]">
              <Upload size={16} />
              Choose Image
              <input
                type="file"
                accept="image/*"
                hidden
                disabled={uploading}
                onChange={async (e) => {
                  const file = e.target.files?.[0];

                  if (!file) return;

                  try {
                    setUploading(true);

                    setPreview(URL.createObjectURL(file));

                    const { imageUrl } = await uploadItemImage({
                      anlookApi,
                      userid: clientId,
                      itemName: "whatsapp_profile",
                      file,
                      maxSizeMB: 5,
                    });

                    if (!imageUrl) {
                      throw new Error("Image URL missing");
                    }

                    const extension = imageUrl.split(".").pop()?.toLowerCase();

                    const uploadedFileType =
                      extension === "webp"
                        ? "image/webp"
                        : extension === "png"
                          ? "image/png"
                          : extension === "jpg" || extension === "jpeg"
                            ? "image/jpeg"
                            : file.type;

                    const profileHandle = await getWhatsappProfileHandle({
                      userid: clientId,
                      filename: imageUrl
                        .split("/")
                        .pop()
                        .replace(/\.[^/.]+$/, ""),
                      filetype: uploadedFileType,
                      fileurl: imageUrl,
                    });

                    if (!profileHandle) {
                      throw new Error("Handle generation failed");
                    }

                    await updateWhatsappProfile({
                      userid: clientId,
                      messaging_product: profile?.messaging_product,
                      about: profile?.about,
                      address: profile?.address,
                      description: profile?.description,
                      vertical: profile?.vertical,
                      email: profile?.email,
                      websites: profile?.websites || [],
                      profile_picture_handle: profileHandle,
                    });

                    const updatedProfile = await fetchProfile(clientId);

                    if (updatedProfile) {
                      setProfile(updatedProfile);
                    }

                    setIsModalOpen(false);

                    toast.success("Profile picture updated");
                  } catch (error) {
                    console.error("Profile picture update failed:", error);

                    toast.error(
                      error?.message || "Failed to update profile picture",
                    );
                  } finally {
                    setUploading(false);
                    e.target.value = "";
                  }
                }}
              />
            </label>

            {uploading && (
              <p className="text-center text-xs mt-4 text-gray-500">
                Uploading & syncing…
              </p>
            )}
          </div>
        </div>
      )}

      {isEditProfileOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 ">
          <div className="w-full max-w-2xl bg-white rounded-2xl p-6 shadow-xl">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold">Edit WhatsApp Profile</h3>
              <button onClick={() => setIsEditProfileOpen(false)}>
                <X size={18} />
              </button>
            </div>

            {/* Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="About"
                name="about"
                value={formData.about}
                onChange={handleChange}
              />
              <Input
                label="Business Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
              <Input
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              <Input
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
              <div>
                <label className="text-[11px] font-bold uppercase text-gray-500 block mb-1">
                  Business Category
                </label>

                <Listbox
                  value={formData.vertical}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      vertical: value,
                    }))
                  }
                >
                  <div className="relative">
                    {/* Button */}
                    <Listbox.Button
                      className="
          relative w-full cursor-pointer rounded-xl border bg-white
          py-2.5 pl-4 pr-10 text-sm text-left
          focus:outline-none focus:ring-2 focus:ring-[#01bf79]
        "
                    >
                      <span className="block truncate">
                        {verticalOptions.find(
                          (v) => v.value === formData.vertical,
                        )?.title || "Select category"}
                      </span>

                      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                        <ChevronDown size={16} className="text-gray-400" />
                      </span>
                    </Listbox.Button>

                    {/* Options */}
                    <Listbox.Options
                      className="
          absolute z-50 mt-2 max-h-52 w-full overflow-y-auto
          rounded-xl bg-white py-1 text-sm shadow-lg border
        "
                    >
                      {verticalOptions.map((item) => (
                        <Listbox.Option
                          key={item.id}
                          value={item.value}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-10 pr-4
              ${active ? "bg-[#01bf79]/10 text-[#075E54]" : "text-gray-700"}`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block truncate ${selected ? "font-semibold" : "font-normal"}`}
                              >
                                {item.title}
                              </span>

                              {selected && (
                                <span className="absolute inset-y-0 left-3 flex items-center text-[#075E54]">
                                  <Check size={16} />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </div>
                </Listbox>
              </div>

              <Input
                label="Messaging Product"
                name="messaging_product"
                value={formData.messaging_product}
                onChange={handleChange}
              />
            </div>

            {/* Official Links */}
            <div className="mt-6">
              <p className="text-xs font-bold uppercase text-gray-500 mb-2">
                Official Links (Max 2)
              </p>

              {formData.websites.map((url, i) => (
                <input
                  key={i}
                  value={url}
                  placeholder={`Website ${i + 1}`}
                  onChange={(e) => handleWebsiteChange(i, e.target.value)}
                  className="w-full mb-3 px-4 py-2 border rounded-xl text-sm"
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setIsEditProfileOpen(false)}
                className="px-4 py-2 rounded-xl border font-semibold"
              >
                Cancel
              </button>

              <button
                onClick={async () => {
                  try {
                    await updateWhatsappProfile({
                      userid: clientId,
                      ...formData,
                      websites: formData.websites.filter(Boolean),
                    });

                    // 🔁 Re-fetch updated profile
                    const updatedProfile = await fetchProfile(clientId);
                    setProfile(updatedProfile);

                    toast.success("Profile updated successfully");
                    setIsEditProfileOpen(false);
                  } catch (error) {
                    toast.error("Failed to update profile");
                  }
                }}
                className="px-5 py-2 rounded-xl bg-[#075E54] text-white font-bold"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- UPDATED COMPONENTS ---------------- */
function Input({ label, ...props }) {
  return (
    <div>
      <label className="mb-0.5 block text-[10px] font-bold uppercase text-gray-500">
        {label}
      </label>
      <input
        {...props}
        className="h-8 w-full rounded-md border px-2 text-[11px] font-semibold outline-none focus:border-[#01bf79] focus:ring-1 focus:ring-[#01bf79]/10"
      />
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className={`rounded-xl border bg-white p-3 shadow-sm ${BORDER_SMART}`}>
      <div className="mb-2.5 flex items-center gap-1.5">
        <div
          className={`flex h-7 w-7 items-center justify-center rounded-md border ${THEME_BLUE_BG} text-[#075E54]`}
        >
          <Icon size={14} />
        </div>

        <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
          {title}
        </div>
      </div>

      <div>{children}</div>
    </div>
  );
}

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="group flex items-start gap-2 rounded-lg p-2 transition-colors hover:bg-[#f8fbff]">
      <div className="mt-0.5 text-gray-400 transition-colors group-hover:text-[#075E54]">
        <Icon size={14} />
      </div>

      <div className="min-w-0">
        <p className="mb-0.5 text-[9px] font-bold uppercase tracking-wide text-gray-400">
          {label}
        </p>
        <p className="break-all text-[11px] font-bold leading-4 text-gray-800">
          {value || "Not provided"}
        </p>
      </div>
    </div>
  );
}

function LoaderSkeleton() {
  return (
    <div className={`space-y-3 p-3 animate-pulse ${THEME_BLUE_BG}`}>
      <div className="h-24 w-full rounded-xl bg-gray-200" />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className={`h-40 rounded-xl border bg-white ${BORDER_SMART}`} />
        <div className={`h-40 rounded-xl border bg-white ${BORDER_SMART}`} />
      </div>
    </div>
  );
}
